import type { LadderRow, LadderTone, OrderBlock } from '../model/ladder.js';

/**
 * The modifier ladder — the §6 ledger's fourth row (design 1545–1600, 372–420).
 *
 * A list of `label · detail · value` rows and a footnote. It knows nothing about
 * missions, orders or success: Phase 4 hands it MW stage credit and Phase 6 the
 * debris table, so the row shape is the contract and the *reading* stays with
 * whoever owns the numbers (`model/ladder.ts` here).
 *
 * The `mod` column is a **string**, not a number, and that is the load-bearing
 * decision for the lift. A ladder that took numbers could not say `NO GO`,
 * `AUTOMATIC`, `TN WITHHELD` or `±?` — and every one of those is a row this
 * console has to draw honestly.
 */

export interface ModifierLadderProps {
  readonly title: string;
  readonly rows: readonly LadderRow[];
  readonly footnote?: string;
  readonly block?: OrderBlock | null;
  readonly onClose?: () => void;
  readonly testId?: string;
  /**
   * Drawn in place rather than over something, so it takes no shadow.
   *
   * The ladder was written as a popover for the order builder and the queue's
   * WHY, and both of those are transient — which is what the one shadow token is
   * for. Phase 4 then used it as a **resting** block in the MW panel and it
   * brought the shadow with it, so the credit ladder has been a floating card on
   * a surface it is part of ever since. Elevation encodes transience here: a
   * thing that is not going away does not get the shadow.
   */
  readonly resting?: boolean;
}

export function ModifierLadder(props: ModifierLadderProps): JSX.Element {
  const resting = props.resting === true;
  return (
    <div
      data-testid={props.testId ?? 'modifier-ladder'}
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        background: 'var(--surface)',
        border: '1px solid ' + (resting ? 'var(--border)' : 'var(--border-strong)'),
        borderRadius: 'var(--radius)',
        ...(resting ? {} : { boxShadow: 'var(--shadow)' }),
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          flex: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          height: 30,
          padding: '0 8px 0 12px',
          background: 'var(--surface-3)',
          borderBottom: '1px solid var(--rule)',
        }}
      >
        <span
          data-testid="ladder-title"
          style={{
            flex: 1,
            minWidth: 0,
            fontFamily: 'var(--font-ui)',
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: '.14em',
            textTransform: 'uppercase',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            color: 'var(--text-2)',
          }}
        >
          {props.title}
        </span>
        {props.onClose !== undefined && (
          <button
            type="button"
            data-testid="ladder-close"
            onClick={props.onClose}
            style={{
              flex: 'none',
              height: 20,
              padding: '0 8px',
              background: 'transparent',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius)',
              color: 'var(--text-muted)',
              fontFamily: 'var(--font-data)',
              fontSize: 11,
              letterSpacing: '.08em',
              cursor: 'pointer',
            }}
          >
            ESC
          </button>
        )}
      </div>

      {props.block != null && <BlockBar block={props.block} />}

      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          padding: '4px 12px 10px',
          overflowY: 'auto',
        }}
      >
        {props.rows.map((row) => (
          <div
            key={row.key}
            data-testid="ladder-row"
            data-ladder-row={row.key}
            style={{
              flex: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              minHeight: 24,
              borderBottom: '1px solid var(--rule-faint)',
            }}
          >
            <span
              style={{
                flex: 'none',
                width: 104,
                fontFamily: 'var(--font-ui)',
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: '.1em',
                textTransform: 'uppercase',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                color: toneColour(row.tone),
              }}
            >
              {row.label}
            </span>
            <span
              data-testid="ladder-detail"
              style={{
                flex: 1,
                minWidth: 0,
                fontFamily: 'var(--font-prose)',
                fontSize: 12,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                color: 'var(--text-muted)',
              }}
            >
              {row.detail}
            </span>
            <span
              data-testid="ladder-mod"
              style={{
                flex: 'none',
                minWidth: 52,
                textAlign: 'right',
                fontFamily: 'var(--font-data)',
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: '.04em',
                whiteSpace: 'nowrap',
                color: toneColour(row.tone),
              }}
            >
              {row.mod}
            </span>
          </div>
        ))}
        {props.footnote !== undefined && (
          <div
            data-testid="ladder-footnote"
            style={{
              flex: 'none',
              paddingTop: 6,
              fontFamily: 'var(--font-prose)',
              fontSize: 11,
              lineHeight: 1.4,
              color: 'var(--text-disabled)',
            }}
          >
            {props.footnote}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * The blocked bar (design 1583–1588).
 *
 * The condition's name and the one sentence that says what changed — the copy
 * rules' shape for an error, and the reason id is the server's own so a block
 * the client drew and a rejection the server sent read as the same thing.
 */
export function BlockBar(props: { readonly block: OrderBlock }): JSX.Element {
  return (
    <div
      data-testid="order-block"
      data-block-reason={props.block.reason}
      style={{
        flex: 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        padding: '7px 12px',
        background: 'var(--red-surface)',
        borderBottom: '1px solid var(--red)',
      }}
    >
      <span
        style={{
          fontFamily: 'var(--font-ui)',
          fontSize: 13,
          fontWeight: 600,
          letterSpacing: '.1em',
          textTransform: 'uppercase',
          color: 'var(--red-text)',
        }}
      >
        {props.block.reason.replace(/_/g, ' ')}
      </span>
      <span
        data-testid="order-block-detail"
        style={{ fontFamily: 'var(--font-prose)', fontSize: 12, color: 'var(--red-text)' }}
      >
        {props.block.detail}
      </span>
    </div>
  );
}

export function toneColour(tone: LadderTone): string {
  switch (tone) {
    case 'good':
      return 'var(--ok)';
    case 'bad':
      return 'var(--red-text)';
    case 'warn':
      return 'var(--warn)';
    default:
      return 'var(--text-2)';
  }
}
